import { useEffect } from 'react'
import { useCentralAPI } from 'src/API/centralAPI'
import { TypographyH3 } from 'src/components/typography'
import { toast } from 'src/shadcn/ui/use-toast'
import { Card } from 'src/shadcn/ui/card'
import { Progress } from 'src/shadcn/ui/progress'

function ReportComponent({ 
    title, 
    id, 
    contiributors, 
    projects, 
    completedWorkItem, 
    totalWorkItem 
}: { 
    title: string
    id: string
    contiributors: number
    projects: number
    totalWorkItem: number
    completedWorkItem: number 
}) {
    const completionRate = totalWorkItem > 0 ? (completedWorkItem / totalWorkItem) * 100 : 0

    return (
        <Card className="p-6 hover:shadow-lg transition-shadow duration-200">
            <div className="flex items-start justify-between">
                <div className="space-y-4">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Organization Overview</p>
                    </div>

                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Contributors</span>
                            <span className="text-sm font-medium text-gray-900 dark:text-white">{contiributors}</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Projects</span>
                            <span className="text-sm font-medium text-gray-900 dark:text-white">{projects}</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Work Items</span>
                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                                {completedWorkItem}/{totalWorkItem}
                            </span>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600 dark:text-gray-400">Progress</span>
                            <span className="font-medium text-gray-900 dark:text-white">{Math.round(completionRate)}%</span>
                        </div>
                        <Progress value={completionRate} className="h-2" />
                    </div>
                </div>

                <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                        <span className="text-xl font-bold text-primary">{title[0]}</span>
                    </div>
                </div>
            </div>
        </Card>
    )
}

function ReportComponentSkeleton() {
    return (
        <Card className="p-6">
            <div className="flex items-start justify-between">
                <div className="space-y-4 w-full">
                    <div>
                        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mt-2"></div>
                    </div>

                    <div className="space-y-3">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="flex items-center justify-between">
                                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
                                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
                            </div>
                        ))}
                    </div>

                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
                            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/6"></div>
                        </div>
                        <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                    </div>
                </div>

                <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-xl"></div>
            </div>
        </Card>
    )
}

export default function Organizations() {
    const {
        centralData,
        isFetching,
        isLoading,
        isError,
        error
    } = useCentralAPI({ trigger: false })

    useEffect(() => {
        if (!isFetching && isError) {
            toast({
                title: 'Error',
                description: error?.message,
            })
        }
    }, [isFetching])

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <TypographyH3>Organizations</TypographyH3>
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {isLoading
                    ? Array.from({ length: 4 }).map((_, index) => (
                        <ReportComponentSkeleton key={index} />
                    ))
                    : centralData?.Organizations?.map((item, index) => (
                        <ReportComponent
                            key={index}
                            title={item.accountName}
                            id={item.accountId}
                            contiributors={item.users.length}
                            projects={item.projects.length}
                            totalWorkItem={item.totalWorkItem}
                            completedWorkItem={item.completedWorkItem}
                        />
                    ))}
            </div>
        </div>
    )
}
